import { useState } from "react";
import { Share, Copy, PaperPlaneTilt, Spinner, Check } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import API from "../lib/api";

/**
 * "Share Tracker" button for admins on Task detail pages.
 *
 * On open:
 *   1. Fetches/generates a tracker_token via POST /tasks/{id}/tracker-token
 *   2. Shows the public URL with Copy + Send Email actions
 *
 * Props:
 *   taskId       - task id (required)
 *   defaultEmail - optional initial email to populate (project client email)
 *   size         - button size (default "sm")
 *   variant      - button variant (default "outline")
 */
export default function ShareTrackerButton({
  taskId,
  defaultEmail = "",
  size = "sm",
  variant = "outline",
  className = "",
  "data-testid": testId = "share-tracker-btn",
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [publicUrl, setPublicUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState(defaultEmail || "");
  const [sending, setSending] = useState(false);

  const openAndLoad = async () => {
    setOpen(true);
    if (token) return; // already loaded
    setLoading(true);
    try {
      const { data } = await API.post(`/tasks/${taskId}/tracker-token`);
      setToken(data.tracker_token);
      const absolute = `${window.location.origin}/track/${data.tracker_token}`;
      setPublicUrl(absolute);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate tracker link");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Tracker link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — long-press to copy manually");
    }
  };

  const handleSend = async () => {
    if (!email.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    setSending(true);
    try {
      const { data } = await API.post(`/tasks/${taskId}/tracker/send-to-client`, { email: email.trim() });
      toast.success(data.message || "Tracker link sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={`w-full ${className}`}
        onClick={openAndLoad}
        data-testid={testId}
      >
        <Share size={14} className="mr-1" />
        Share Live Tracker
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="share-tracker-dialog">
          <DialogHeader>
            <DialogTitle>Share Live Tracker</DialogTitle>
            <DialogDescription>
              Send the client a read-only link to follow the supplier's arrival in real time.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-10 flex items-center justify-center text-muted-foreground text-sm">
              <Spinner size={16} className="animate-spin mr-2" /> Generating link...
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs">Public tracker URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    readOnly
                    value={publicUrl}
                    onFocus={(e) => e.target.select()}
                    data-testid="tracker-url-input"
                    className="font-mono text-xs"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCopy}
                    data-testid="copy-tracker-url-btn"
                    className="shrink-0"
                  >
                    {copied ? <Check size={14} className="mr-1 text-emerald-500" /> : <Copy size={14} className="mr-1" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">Anyone with this link can view status — no login required.</p>
              </div>

              <div className="border-t border-border pt-4">
                <Label htmlFor="tracker-send-email" className="text-xs">Send to client email</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="tracker-send-email"
                    type="email"
                    placeholder="client@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="tracker-send-email-input"
                  />
                  <Button
                    type="button"
                    onClick={handleSend}
                    disabled={sending || !email.trim()}
                    data-testid="tracker-send-email-btn"
                    className="shrink-0"
                  >
                    {sending ? <Spinner size={14} className="animate-spin mr-1" /> : <PaperPlaneTilt size={14} className="mr-1" />}
                    Send
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
